// Reusable form layout: grouped sections + sticky action bar with Cancel / Save and New / Save.
import type { ReactNode } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";

interface SectionProps { title: string; children: ReactNode; }
export function Section({ title, children }: SectionProps) {
  return (
    <Card>
      <CardHeader className="pb-3"><CardTitle className="text-sm">{title}</CardTitle></CardHeader>
      <CardContent className="grid gap-4 pt-0 md:grid-cols-2">{children}</CardContent>
    </Card>
  );
}

interface FieldProps { id: string; label: string; required?: boolean; className?: string; children: ReactNode; }
export function Field({ id, label, required, className, children }: FieldProps) {
  return (
    <div className={className}>
      <Label htmlFor={id}>
        {label}{required && <span className="ml-0.5 text-destructive">*</span>}
      </Label>
      {children}
    </div>
  );
}

interface ActionBarProps {
  busy: boolean;
  canSubmit: boolean;
  onCancel: () => void;
  onSubmit?: () => void;           // If omitted, rely on the form's submit button.
  onSaveAndNew?: () => void;       // If provided, renders a "Save and New" button.
  error?: string | null;
}

export function ActionBar({ busy, canSubmit, onCancel, onSubmit, onSaveAndNew, error }: ActionBarProps) {
  return (
    <div className="sticky bottom-0 flex items-center justify-between rounded-md border bg-card px-6 py-4 shadow-sm">
      {error ? <p className="text-sm text-destructive">{error}</p> : <span />}
      <div className="flex gap-2">
        <Button type="button" variant="outline" onClick={onCancel}>Cancel</Button>
        {onSaveAndNew && (
          <Button
            type="button"
            variant="outline"
            disabled={busy || !canSubmit}
            onClick={onSaveAndNew}
          >
            {busy ? "Saving…" : "Save and New"}
          </Button>
        )}
        <Button type={onSubmit ? "button" : "submit"} onClick={onSubmit} disabled={busy || !canSubmit}>
          {busy ? "Saving…" : "Save"}
        </Button>
      </div>
    </div>
  );
}
